import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ClientCombobox } from '@/components/shared';
import {
  MODELO_EMAIL_CLIENTE_SUGERIDO,
  MOMENTO_EMAIL_CLIENTE_LABELS,
  VARIAVEIS_EMAIL_CLIENTE,
  emailValido,
  listaEmails,
  preencherEmailCliente,
} from '../../../models/emailCliente.modelos';
import type { ModeloEmailClienteSalvar, MomentoEmailCliente } from '../../../models/emailCliente.modelos';
import type { TipoSolicitacao } from '../../../models';
import { TIPO_SOLICITACAO_LABELS } from '../../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';

interface EmailClienteModeloEditorProps {
  inicial: ModeloEmailClienteSalvar;
  salvando: boolean;
  onSalvar: (modelo: ModeloEmailClienteSalvar) => void;
  onExcluir?: () => void;
}

/** Tipos que fazem sentido pra um e-mail de entrada/saída no Cliente. */
const TIPOS_DISPONIVEIS: TipoSolicitacao[] = ['admissao', 'nova_demanda', 'mudanca_projeto', 'desligamento', 'devolucao', 'troca_equipamento'];

const EXEMPLO: Record<string, string> = {
  saudacao: 'bom dia',
  nome: 'Ana Ribeiro Souza',
  funcao: 'Desenvolvedora',
  email_raro: 'ana.souza@rarolabs.com.br',
  email_pessoal: 'ana@gmail.com',
  telefone: '(31) 99999-0000',
  cpf: '000.000.000-00',
  rg: 'MG-00.000.000',
  data_nascimento: '10/05/1995',
  endereco: 'Rua das Flores, 120 - Centro, Belo Horizonte/MG - CEP 30110-000',
  so: 'Windows',
  gestor_raro: 'Gestor Raro',
  gestor_cliente: 'Gestor no cliente',
  contrato_cliente: 'Contrato X',
  projeto_cliente: 'Squad Y',
  data_inicio: '04/11/2026',
  data_termino: '30/11/2026',
};

const textoLista = (emails: string[]) => emails.join(', ');

/** Formulário de um modelo de e-mail para o Cliente — o pai remonta com `key` ao trocar de modelo. */
export default function EmailClienteModeloEditor({ inicial, salvando, onSalvar, onExcluir }: EmailClienteModeloEditorProps) {
  const [modelo, setModelo] = useState(inicial);
  const [para, setPara] = useState(textoLista(inicial.para));
  const [cc, setCc] = useState(textoLista(inicial.cc));
  const corpoRef = useRef<HTMLTextAreaElement>(null);
  const atualizar = (parcial: Partial<ModeloEmailClienteSalvar>) => setModelo((atual) => ({ ...atual, ...parcial }));
  const novo = !inicial.id;

  const listaPara = listaEmails(para);
  const listaCc = listaEmails(cc);
  const invalidos = [...listaPara, ...listaCc].filter((email) => !emailValido(email));
  const valido =
    Boolean(modelo.clienteId) &&
    listaPara.length > 0 &&
    invalidos.length === 0 &&
    modelo.tiposSolicitacao.length > 0 &&
    modelo.assunto.trim() !== '' &&
    modelo.corpo.trim() !== '';

  const previa = useMemo(() => {
    const valores = { ...EXEMPLO, cliente: modelo.clienteNome || 'Cliente' };
    return { assunto: preencherEmailCliente(modelo.assunto, valores), corpo: preencherEmailCliente(modelo.corpo, valores) };
  }, [modelo.assunto, modelo.corpo, modelo.clienteNome]);

  // Modelo novo: trocar Entrada/Saída troca o texto sugerido (enquanto não foi editado).
  const trocarMomento = (momento: MomentoEmailCliente) => {
    const sugeridoAtual = MODELO_EMAIL_CLIENTE_SUGERIDO[modelo.momento];
    const intocado = modelo.corpo === sugeridoAtual.corpo && modelo.assunto === sugeridoAtual.assunto;
    atualizar(novo && intocado ? { momento, ...MODELO_EMAIL_CLIENTE_SUGERIDO[momento] } : { momento });
  };

  const inserir = (chave: string) => {
    const campo = corpoRef.current;
    const inicio = campo?.selectionStart ?? modelo.corpo.length;
    const fim = campo?.selectionEnd ?? modelo.corpo.length;
    atualizar({ corpo: modelo.corpo.slice(0, inicio) + chave + modelo.corpo.slice(fim) });
    requestAnimationFrame(() => {
      campo?.focus();
      campo?.setSelectionRange(inicio + chave.length, inicio + chave.length);
    });
  };

  const alternarTipo = (tipo: TipoSolicitacao) =>
    atualizar({
      tiposSolicitacao: modelo.tiposSolicitacao.includes(tipo)
        ? modelo.tiposSolicitacao.filter((t) => t !== tipo)
        : [...modelo.tiposSolicitacao, tipo],
    });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Cliente *</label>
          <div className="mt-1">
            <ClientCombobox
              selectedId={modelo.clienteId || null}
              placeholder="Buscar cliente..."
              onChange={(client) => atualizar({ clienteId: client?.id ?? '', clienteNome: client?.name ?? '' })}
            />
          </div>
        </div>
        <label className="block text-sm font-medium text-gray-700">
          Momento *
          <select
            className="input-primary mt-1 w-full"
            value={modelo.momento}
            onChange={(event) => trocarMomento(event.target.value as MomentoEmailCliente)}
          >
            {Object.entries(MOMENTO_EMAIL_CLIENTE_LABELS).map(([valor, label]) => (
              <option key={valor} value={valor}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <fieldset>
        <legend className="text-sm font-medium text-gray-700">Aparece nas Solicitações do tipo *</legend>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {TIPOS_DISPONIVEIS.map((tipo) => {
            const marcado = modelo.tiposSolicitacao.includes(tipo);
            return (
              <label
                key={tipo}
                className={clsx(
                  'flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs',
                  marcado ? 'border-primary-300 bg-primary-50 font-semibold text-primary-800' : 'border-gray-200 text-gray-700'
                )}
              >
                <input type="checkbox" checked={marcado} onChange={() => alternarTipo(tipo)} />
                {TIPO_SOLICITACAO_LABELS[tipo]}
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm font-medium text-gray-700">
          Para *
          <textarea className="input-primary mt-1 w-full text-sm" rows={2} value={para} onChange={(e) => setPara(e.target.value)} />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Cópia
          <textarea className="input-primary mt-1 w-full text-sm" rows={2} value={cc} onChange={(e) => setCc(e.target.value)} />
        </label>
      </div>
      <p className="-mt-2 text-[11px] text-gray-500">
        Separe por vírgula. Pode colar do Outlook (&quot;Nome &lt;email&gt;&quot;).
        {invalidos.length > 0 && <span className="ml-1 font-semibold text-red-600">E-mail inválido: {invalidos.join(', ')}</span>}
      </p>

      <label className="block text-sm font-medium text-gray-700">
        Assunto *
        <input className="input-primary mt-1 w-full" value={modelo.assunto} onChange={(e) => atualizar({ assunto: e.target.value })} />
      </label>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="email-cliente-corpo" className="block text-sm font-medium text-gray-700">
            Texto do e-mail *
          </label>
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <span className="text-gray-500">Inserir:</span>
            {VARIAVEIS_EMAIL_CLIENTE.map((v) => (
              <button
                key={v.chave}
                type="button"
                onClick={() => inserir(`{${v.chave}}`)}
                title={v.origem === 'abertura' ? 'Pedido a quem abre a Solicitação' : 'O Digital preenche'}
                className={clsx(
                  'rounded-full px-2 py-0.5 font-semibold',
                  v.origem === 'abertura' ? 'bg-amber-50 text-amber-800 hover:bg-amber-100' : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
          <textarea
            id="email-cliente-corpo"
            ref={corpoRef}
            rows={16}
            className="input-primary w-full font-mono text-xs"
            value={modelo.corpo}
            onChange={(e) => atualizar({ corpo: e.target.value })}
          />
          <p className="text-[11px] text-gray-500">
            Em amarelo: o Digital não tem esse dado — é pedido a quem abre a Solicitação, no bloco &quot;Dados para o cliente&quot;.
          </p>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Prévia (dados fictícios)</p>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-[13px] text-gray-900">
            <p className="border-b border-gray-200 pb-2 font-semibold">{previa.assunto || 'Sem assunto'}</p>
            <p className="whitespace-pre-wrap pt-2 leading-relaxed">{previa.corpo}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
        {onExcluir ? (
          <button type="button" className="text-xs font-semibold text-gray-500 hover:text-red-600" onClick={onExcluir}>
            🗑 Excluir modelo
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="btn-primary"
          disabled={!valido || salvando}
          onClick={() => onSalvar({ ...modelo, para: listaPara, cc: listaCc, assunto: modelo.assunto.trim() })}
        >
          {salvando ? 'Salvando...' : 'Salvar modelo'}
        </button>
      </div>
    </div>
  );
}
